// Resizes/re-encodes large images client-side before upload. Phone camera photos are commonly
// 3-8MB, which uploads fine on WiFi but is exactly the kind of payload that times out or drops
// mid-transfer on slow/flaky mobile data - shrinking it client-side (well before it ever hits the
// network) is the fix, not anything server-side, since the failure happens on the way up.
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.8;
const COMPRESS_THRESHOLD_BYTES = 500 * 1024; // small images aren't worth re-encoding

function compressImageFile(file) {
    return new Promise((resolve) => {
        if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.size <= COMPRESS_THRESHOLD_BYTES) {
            resolve(file);
            return;
        }

        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            let { width, height } = img;
            if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                if (width > height) {
                    height = Math.round(height * (MAX_DIMENSION / width));
                    width = MAX_DIMENSION;
                } else {
                    width = Math.round(width * (MAX_DIMENSION / height));
                    height = MAX_DIMENSION;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);

            canvas.toBlob((blob) => {
                // Rare, but if re-encoding didn't actually shrink it (e.g. a already-efficient
                // JPEG), keep the original rather than uploading a larger re-encoded copy.
                if (!blob || blob.size >= file.size) {
                    resolve(file);
                    return;
                }
                resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
            }, 'image/jpeg', JPEG_QUALITY);
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(file); // fall back to the original rather than blocking the upload entirely
        };

        img.src = objectUrl;
    });
}

function initializeImageCompression() {
    document.querySelectorAll('input[type=file].compress-image').forEach((input) => {
        if (input.dataset.compressionWired) return;
        input.dataset.compressionWired = 'true';

        input.addEventListener('change', async () => {
            if (!input.files || input.files.length === 0) return;

            const compressed = await Promise.all(Array.from(input.files).map(compressImageFile));

            const dt = new DataTransfer();
            compressed.forEach((file) => dt.items.add(file));
            input.files = dt.files;
        });
    });
}

// A slow/flaky mobile-data upload can take a while even after compression - without feedback a
// driver might tap the submit button again, or back out thinking it's frozen. Any submit button
// with data-uploading-text gets disabled and re-labeled once its form is actually submitted.
function initializeUploadFeedback() {
    document.querySelectorAll('form').forEach((form) => {
        if (form.dataset.uploadFeedbackWired) return;

        const button = form.querySelector('[data-uploading-text]');
        if (!button) return;

        form.dataset.uploadFeedbackWired = 'true';
        form.addEventListener('submit', () => {
            button.disabled = true;
            // f.submit renders <input type="submit">, not <button> - inputs show their label via
            // the value attribute, not textContent, which has no visual effect on them.
            if (button.tagName === 'INPUT') {
                button.value = button.dataset.uploadingText;
            } else {
                button.textContent = button.dataset.uploadingText;
            }
        });
    });
}

// Called eagerly here (not only from all_pages.js's turbo:load handler) for the same reason as
// ckeditor5_setup.js: this file loads as an ES module, which browsers always defer relative to
// HTML parsing, so on a first page load turbo:load can fire and be missed before the listener in
// all_pages.js even attaches. Safe to call from both places - both functions guard per-element/
// per-form against re-wiring.
document.addEventListener('turbo:load', () => {
    initializeImageCompression();
    initializeUploadFeedback();
});
initializeImageCompression();
initializeUploadFeedback();

export { initializeImageCompression, initializeUploadFeedback, compressImageFile };
